
import React, { useEffect, useState } from "react";

import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";

import TableStyle from "./TableStyle";

import {
    Table,
    Input,
    Button,
    Typography,
    Space,
    Empty,
    Tag,
    message,
    Popconfirm,
    Tooltip,
} from "antd";

import {
    SearchOutlined,
    SafetyOutlined,
    EditOutlined,
    DeleteOutlined,
    UserOutlined,
    ReloadOutlined,
    TeamOutlined,
    FileTextOutlined,
} from "@ant-design/icons";

import Secure from "./Secure";
import EditStudentAdmissionPopup from "./EditStudentAdmissionPopup";
import AdmissionStudentDetailsPopUp from "./AdmissionStudentDetailsPopUp";
import AdmissionStuListByClass from "./AdmissionStuListByClass";
import AdmissionDataFormDownload from "./AdmissionDataFormDownload";

const { Title, Text } = Typography;

/* ================================================================
   PAGE LOADER
================================================================ */

const PageLoader = () => (
    <div className="students-admission-loader">
        <div className="students-admission-loader-ring">
            <span />
            <span />
            <span />
        </div>

        <Text>Loading student admissions...</Text>
    </div>
);

/* ================================================================
   IMAGE HELPERS
================================================================ */

const getStudentImageValue = (student) => {
    const candidates = [
        student?.student_admission_image,
        student?.student_image,
        student?.profile_image,
        student?.profile_picture,
        student?.photo,
        student?.image,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim();
        }

        if (candidate && typeof candidate === "object") {
            const url = candidate.url || candidate.image;

            if (typeof url === "string" && url.trim()) {
                return url.trim();
            }
        }
    }

    return "";
};

const getStudentImageUrl = (student) => {
    const imagePath = getStudentImageValue(student);

    if (!imagePath) return "";

    if (
        imagePath.startsWith("data:") ||
        imagePath.startsWith("blob:")
    ) {
        return imagePath;
    }

    try {
        const configuredBase =
            api.defaults?.baseURL ||
            process.env.REACT_APP_API_URL ||
            process.env.REACT_APP_API_BASE_URL ||
            window.location.origin;

        const apiBase = configuredBase.replace(/\/+$/, "");

        const mediaBase = apiBase.replace(/\/api\/?$/, "");

        return new URL(imagePath, `${mediaBase}/`).href;
    } catch (error) {
        console.warn("Unable to resolve student image URL:", error);
        return "";
    }
};

/* ================================================================
   STUDENTS ADMISSION
================================================================ */

const StudentsAdmission = () => {
    const { selectedYear } = useAcademicYear();

    const [secureStudent, setSecureStudent] = useState(null);

    const [students, setStudents] = useState([]);

    const [loading, setLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");

    const [editStudent, setEditStudent] = useState(null);

    // Selected student whose portfolio is currently open.
    const [selectedStudentDetails, setSelectedStudentDetails] =
        useState(null);

    const [darkMode, setDarkMode] = useState(
        () => localStorage.getItem("darkMode") === "true"
    );

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
    });

    const getToken = () => localStorage.getItem("access");

    /* ============================================================
       DARK MODE OBSERVER
    ============================================================ */

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setDarkMode(
                document.body.classList.contains("dark-mode")
            );
        });

        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["class"],
        });

        return () => observer.disconnect();
    }, []);

    /* ============================================================
       FETCH STUDENTS

       Preserves:
       - Existing Django API endpoint
       - Academic-year filtering
       - Server-side global search
       - Pagination
    ============================================================ */

    const fetchStudents = async (
        page = 1,
        pageSize = pagination.pageSize,
        requestedSearch = searchTerm
    ) => {
        const token = getToken();

        if (!token) {
            window.location.href = "/login";
            return;
        }

        if (!selectedYear?.id) {
            setStudents([]);

            setPagination({
                current: 1,
                pageSize,
                total: 0,
            });

            return;
        }

        try {
            setLoading(true);

            const cleanSearch = String(
                requestedSearch || ""
            ).trim();

            const params = new URLSearchParams();

            params.set("academic_year", selectedYear.id);
            params.set("page", page);
            params.set("page_size", pageSize);

            if (cleanSearch) {
                params.set("search", cleanSearch);
            }

            const response = await api.get(
                `/api/users/students/?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const responseData = response.data;

            const results = Array.isArray(responseData?.results)
                ? responseData.results
                : Array.isArray(responseData)
                    ? responseData
                    : [];

            setStudents(results);

            setPagination({
                current:
                    Number(responseData?.current_page) || page,

                pageSize:
                    Number(responseData?.page_size) || pageSize,

                total:
                    Number(
                        responseData?.count ??
                        responseData?.total ??
                        results.length
                    ) || 0,
            });
        } catch (error) {
            console.error(
                "Student admissions fetch error:",
                error
            );

            if (error.response?.status === 401) {
                localStorage.removeItem("access");
                window.location.href = "/login";
                return;
            }

            message.error(
                error.response?.data?.detail ||
                "Unable to load student admissions."
            );
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       INITIAL LOAD / ACADEMIC YEAR CHANGE
    ============================================================ */

    useEffect(() => {
        if (!selectedYear?.id) {
            setStudents([]);

            setPagination({
                current: 1,
                pageSize: 50,
                total: 0,
            });

            return;
        }

        fetchStudents(1, pagination.pageSize, searchTerm);

        // Academic-year changes should reload the records.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear?.id]);

    /* ============================================================
       GLOBAL SERVER-SIDE SEARCH

       Debounced to avoid sending a request on every keystroke.
    ============================================================ */

    useEffect(() => {
        if (!selectedYear?.id) return;

        const timer = setTimeout(() => {
            fetchStudents(1, pagination.pageSize, searchTerm);
        }, 350);

        return () => clearTimeout(timer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    /* ============================================================
       OPEN / CLOSE STUDENT PORTFOLIO
    ============================================================ */

    const openStudentPortfolio = (student) => {
        if (!student) return;

        setSelectedStudentDetails(student);
    };

    const closeStudentPortfolio = () => {
        setSelectedStudentDetails(null);
    };

    /* ============================================================
       EDIT FROM PORTFOLIO

       Close the portfolio and open the existing editor for the
       exact same student. The editor component is not replaced.
    ============================================================ */

    const editStudentFromPortfolio = (student) => {
        if (!student) return;

        setSelectedStudentDetails(null);
        setEditStudent(student);
    };

    /* ============================================================
       DELETE STUDENT
    ============================================================ */

    const deleteStudent = async (student) => {
        try {
            const token = getToken();

            if (!token) {
                window.location.href = "/login";
                return;
            }

            await api.delete(
                `/api/users/students/${student.id}/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            message.success(
                "Student admission deleted successfully."
            );

            // Close the portfolio if the deleted student was open.
            if (
                String(selectedStudentDetails?.id) ===
                String(student.id)
            ) {
                setSelectedStudentDetails(null);
            }

            // Close the editor if it belongs to the deleted student.
            if (
                String(editStudent?.id) === String(student.id)
            ) {
                setEditStudent(null);
            }

            const nextPage =
                students.length === 1 && pagination.current > 1
                    ? pagination.current - 1
                    : pagination.current;

            fetchStudents(
                nextPage,
                pagination.pageSize,
                searchTerm
            );
        } catch (error) {
            console.error("Delete student error:", error);

            if (error.response?.status === 401) {
                localStorage.removeItem("access");
                window.location.href = "/login";
                return;
            }

            message.error(
                error.response?.data?.detail ||
                "Unable to delete student."
            );
        }
    };

    /* ============================================================
       TABLE COLUMNS
    ============================================================ */

    const columns = [
        {
            title: "NO.",
            width: 65,
            fixed: "left",
            align: "center",

            render: (_, __, index) => {
                const hasSearch = searchTerm.trim().length > 0;

                if (hasSearch) {
                    return index + 1;
                }

                return (
                    (pagination.current - 1) *
                        pagination.pageSize +
                    index +
                    1
                );
            },
        },

        /* ========================================================
           STUDENT PROFILE

           Clicking either the avatar or the name opens the
           AdmissionStudentDetailsPopUp for that student.
        ======================================================== */

        {
            title: "STUDENT",
            dataIndex: "full_name",
            width: 285,

            render: (text, student) => {
                const studentName =
                    text ||
                    student?.student_name ||
                    student?.name ||
                    "Unnamed Student";

                const photoUrl = getStudentImageUrl(student);

                return (
                    <button
                        type="button"
                        className="student-name-cell student-profile-trigger"
                        title={`View ${studentName}'s admission portfolio`}
                        aria-label={`View admission details for ${studentName}`}
                        onClick={() => openStudentPortfolio(student)}
                    >
                        <span className="student-avatar">
                            <UserOutlined className="student-avatar-fallback" />

                            {photoUrl && (
                                <img
                                    src={photoUrl}
                                    alt=""
                                    className="student-table-photo"
                                    loading="lazy"
                                    onError={(event) => {
                                        event.currentTarget.style.display =
                                            "none";
                                    }}
                                />
                            )}
                        </span>

                        <span className="student-name-content">
                            <strong>{studentName}</strong>

                            <span>
                                Student Admission · View Portfolio
                            </span>
                        </span>
                    </button>
                );
            },
        },

        {
            title: "SESSION",
            dataIndex: "division_assigned_name",
            responsive: ["md"],
            width: 170,

            render: (text) => (
                <span className="table-secondary-text">
                    {text || "—"}
                </span>
            ),
        },

        {
            title: "GRADE CLASS",
            dataIndex: "grade_class_name",
            width: 180,

            render: (text) => (
                <Tag className="grade-class-tag">
                    <TeamOutlined />
                    {text || "Not Assigned"}
                </Tag>
            ),
        },

        {
            title: "SEMESTER",
            dataIndex: "semester_status",
            responsive: ["lg"],
            width: 130,

            render: (text) => (
                <span className="table-secondary-text">
                    {text || "—"}
                </span>
            ),
        },

        {
            title: "STATUS",
            dataIndex: "status",
            width: 110,
            align: "center",

            render: (status) => {
                const active = [
                    "enrolled",
                    "active",
                    "admitted",
                ].includes(String(status || "").toLowerCase());

                return (
                    <Tag
                        className={
                            active
                                ? "status-active"
                                : "status-inactive"
                        }
                    >
                        {status || "Unknown"}
                    </Tag>
                );
            },
        },

        {
            title: "ACTIONS",
            fixed: "right",
            width: 245,

            render: (_, student) => (
                <Space
                    size={6}
                    className="student-action-space"
                >
                    <Tooltip title="Edit admission">
                        <Button
                            className="table-action-edit"
                            icon={<EditOutlined />}
                            onClick={() => setEditStudent(student)}
                        >
                            Edit
                        </Button>
                    </Tooltip>

                    <Popconfirm
                        title="Delete Student Admission"
                        description={
                            `Are you sure you want to delete ${
                                student.full_name ||
                                student.student_name ||
                                "this student"
                            }?`
                        }
                        okText="Yes, Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteStudent(student)}
                    >
                        <Tooltip title="Delete admission">
                            <Button
                                danger
                                className="table-action-delete"
                                icon={<DeleteOutlined />}
                            >
                                Delete
                            </Button>
                        </Tooltip>
                    </Popconfirm>

                    <Tooltip title="Student authentication">
                        <Button
                            className="secure-btn"
                            type="primary"
                            icon={<SafetyOutlined />}
                            onClick={() => {
                                setSecureStudent(student);

                                setTimeout(() => {
                                    setSecureStudent(null);
                                }, 1000);
                            }}
                        >
                            Auth
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    /* ================================================================
       RENDER
    ================================================================ */

    return (
        <div
            className={`students-admission-page ${
                darkMode ? "students-admission-dark" : ""
            }`}
        >
            <TableStyle />

            <div className="students-admission-shell">
                {/* =========================
                    HEADER
                ========================= */}

                <section className="students-admission-header">
                    <div className="students-admission-header-glow" />

                    <div className="students-admission-header-content">
                        <div className="students-admission-title-group">
                            <div className="students-admission-icon">
                                <TeamOutlined />
                            </div>

                            <div>
                                <Text className="students-admission-eyebrow">
                                    Admissions Management
                                </Text>

                                <Title
                                    level={2}
                                    className="students-admission-title"
                                >
                                    Student Admissions
                                </Title>

                                <Text className="students-admission-subtitle">
                                    Manage registered students, admission
                                    records and academic placement.
                                </Text>
                            </div>
                        </div>

                        <div className="students-admission-year">
                            <span>Academic Year</span>

                            <strong>
                                {selectedYear?.name || "Select Year"}
                            </strong>
                        </div>
                    </div>
                </section>

                {/* =========================
                    DOWNLOAD / CLASS SECTION
                ========================= */}

                <section className="admission-download-section">
                    <AdmissionStuListByClass />
                </section>

                {/* =========================
                    TOOLBAR
                ========================= */}

                <section className="students-admission-toolbar">
                    <div className="students-admission-toolbar-left">
                        <div className="students-admission-search">
                            <SearchOutlined />

                            <Input
                                bordered={false}
                                allowClear
                                placeholder="Search student, class, email or status..."
                                value={searchTerm}
                                onChange={(event) => {
                                    setSearchTerm(event.target.value);
                                }}
                            />
                        </div>

                        {searchTerm && (
                            <Tag className="search-result-tag">
                                Searching: {searchTerm}
                            </Tag>
                        )}
                    </div>

                    <div className="students-admission-toolbar-actions">
                        <Tooltip title="Refresh admissions">
                            <Button
                                className="refresh-admission-btn"
                                icon={<ReloadOutlined />}
                                loading={loading}
                                onClick={() =>
                                    fetchStudents(
                                        pagination.current,
                                        pagination.pageSize,
                                        searchTerm
                                    )
                                }
                            />
                        </Tooltip>

                        <AdmissionDataFormDownload />
                    </div>
                </section>

                {/* =========================
                    TABLE
                ========================= */}

                <section className="students-admission-table-section">
                    <div className="students-admission-table-heading">
                        <div>
                            <Text className="table-heading-eyebrow">
                                Registered Students
                            </Text>

                            <div className="table-heading-title">
                                Admission Records
                            </div>
                        </div>

                        <div className="table-record-count">
                            <FileTextOutlined />

                            <span>
                                {pagination.total || 0} Records
                            </span>
                        </div>
                    </div>

                    {loading ? (
                        <PageLoader />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={students}
                            rowKey="id"
                            className="students-admission-table"
                            rowClassName={() => "student-admission-row"}
                            scroll={{ x: 1100 }}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                pageSizeOptions: [
                                    "10",
                                    "20",
                                    "50",
                                    "100",
                                ],
                                showTotal: (total) =>
                                    `Total ${total} students`,
                                onChange: (page, pageSize) => {
                                    fetchStudents(
                                        page,
                                        pageSize,
                                        searchTerm
                                    );
                                },
                            }}
                            locale={{
                                emptyText: (
                                    <Empty
                                        image={
                                            Empty.PRESENTED_IMAGE_SIMPLE
                                        }
                                        description={
                                            searchTerm
                                                ? "No students match your search."
                                                : "No student admissions found."
                                        }
                                    />
                                ),
                            }}
                        />
                    )}
                </section>
            </div>

            {/* ============================================================
                STUDENT PORTFOLIO POPUP
            ============================================================ */}

            <AdmissionStudentDetailsPopUp
                visible={!!selectedStudentDetails}
                student={selectedStudentDetails}
                darkMode={darkMode}
                onClose={closeStudentPortfolio}
                onEdit={editStudentFromPortfolio}
            />

            {/* ============================================================
                SECURE STUDENT
            ============================================================ */}

            {secureStudent && (
                <Secure student={secureStudent} />
            )}

            {/* ============================================================
                EXISTING EDIT STUDENT POPUP

                This remains your existing component.
            ============================================================ */}

            <EditStudentAdmissionPopup
                visible={!!editStudent}
                student={editStudent}
                onClose={() => setEditStudent(null)}
                refresh={() =>
                    fetchStudents(
                        pagination.current,
                        pagination.pageSize,
                        searchTerm
                    )
                }
            />

            {/* ============================================================
                STYLES
            ============================================================ */}

            <style>{`
                .students-admission-page {
                    --admission-navy: #062A4B;
                    --admission-blue: #063A6B;
                    --admission-orange: #C0750D;
                    --admission-gold: #F6C944;
                    --admission-yellow: #EFA81C;
                    --admission-page: #F4F7F9;
                    --admission-white: #FFFFFF;
                    --admission-text: #142536;
                    --admission-muted: #6B7280;
                    --admission-border: #E0E7EC;
                    --admission-green: #4FAE22;

                    width: 100%;
                    min-height: 100vh;
                    padding: 18px;
                    box-sizing: border-box;
                    background: linear-gradient(
                        180deg,
                        #f7f9fb 0%,
                        #f2f5f8 100%
                    );
                }

                .students-admission-page *,
                .students-admission-page *::before,
                .students-admission-page *::after {
                    box-sizing: border-box;
                }

                .students-admission-dark {
                    --admission-page: #0B1117;
                    --admission-white: #141C24;
                    --admission-text: #F0F4F8;
                    --admission-muted: #A6B0BA;
                    --admission-border: #273541;

                    background: linear-gradient(
                        180deg,
                        #0b1117 0%,
                        #0e151c 100%
                    );
                }

                .students-admission-shell {
                    width: 100%;
                    max-width: 1800px;
                    margin: 0 auto;
                }

                /* HEADER */

                .students-admission-header {
                    position: relative;
                    overflow: hidden;
                    min-height: 145px;
                    margin-bottom: 14px;
                    border-radius: 22px;
                    background: linear-gradient(
                        120deg,
                        #062A4B 0%,
                        #063A6B 58%,
                        #0F6DAB 100%
                    );
                    box-shadow: 0 16px 38px rgba(6,42,75,.16);
                }

                .students-admission-header-glow {
                    position: absolute;
                    width: 260px;
                    height: 260px;
                    right: -80px;
                    top: -145px;
                    border-radius: 50%;
                    background: rgba(246,201,68,.15);
                    pointer-events: none;
                }

                .students-admission-header::after {
                    content: "";
                    position: absolute;
                    width: 160px;
                    height: 160px;
                    left: 42%;
                    bottom: -130px;
                    border-radius: 50%;
                    background: rgba(255,255,255,.045);
                    pointer-events: none;
                }

                .students-admission-header-content {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 25px;
                    min-height: 145px;
                    padding: 25px 28px;
                }

                .students-admission-title-group {
                    display: flex;
                    align-items: center;
                    gap: 17px;
                    min-width: 0;
                }

                .students-admission-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 58px;
                    height: 58px;
                    min-width: 58px;
                    border: 1px solid rgba(255,255,255,.18);
                    border-radius: 16px;
                    color: #F6C944;
                    background: rgba(255,255,255,.12);
                    font-size: 26px;
                    box-shadow: inset 0 1px 0 rgba(255,255,255,.08);
                }

                .students-admission-eyebrow {
                    display: block;
                    margin-bottom: 7px;
                    color: #F6C944;
                    font-size: 10px;
                    line-height: 1;
                    font-weight: 800;
                    letter-spacing: 1.4px;
                    text-transform: uppercase;
                }

                .students-admission-title {
                    margin: 0 !important;
                    color: #fff !important;
                    font-size: 28px !important;
                    line-height: 1.08 !important;
                    font-weight: 800 !important;
                    letter-spacing: -.6px;
                }

                .students-admission-subtitle {
                    display: block;
                    margin-top: 7px;
                    color: rgba(255,255,255,.72);
                    font-size: 12.5px;
                    line-height: 1.5;
                }

                .students-admission-year {
                    min-width: 175px;
                    padding: 13px 17px;
                    border: 1px solid rgba(255,255,255,.13);
                    border-radius: 13px;
                    background: rgba(0,0,0,.15);
                    backdrop-filter: blur(8px);
                }

                .students-admission-year span {
                    display: block;
                    margin-bottom: 5px;
                    color: rgba(255,255,255,.58);
                    font-size: 9px;
                    font-weight: 700;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                }

                .students-admission-year strong {
                    display: block;
                    color: #fff;
                    font-size: 15px;
                }

                /* TOOLBAR */

                .students-admission-toolbar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 14px;
                    min-height: 72px;
                    padding: 12px 15px;
                    margin-bottom: 14px;
                    border: 1px solid var(--admission-border);
                    border-radius: 16px;
                    background: var(--admission-white);
                    box-shadow: 0 6px 22px rgba(6,42,75,.055);
                }

                .students-admission-toolbar-left {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 0;
                    flex: 1;
                }

                .students-admission-search {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    width: min(520px, 100%);
                    height: 43px;
                    padding: 0 13px;
                    border: 1px solid var(--admission-border);
                    border-radius: 11px;
                    color: var(--admission-muted);
                    background: var(--admission-page);
                }

                .students-admission-search .ant-input {
                    color: var(--admission-text);
                    background: transparent;
                    font-size: 13px;
                }

                .students-admission-search .ant-input::placeholder {
                    color: var(--admission-muted);
                }

                .search-result-tag {
                    margin: 0;
                    border: 0;
                    border-radius: 8px;
                    color: #C0750D;
                    background: #FFF7E6;
                }

                .students-admission-toolbar-actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-shrink: 0;
                }

                .refresh-admission-btn {
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    color: var(--admission-navy);
                    border-color: var(--admission-border);
                    background: var(--admission-white);
                }

                /* TABLE CONTAINER */

                .students-admission-table-section {
                    overflow: hidden;
                    border: 1px solid var(--admission-border);
                    border-radius: 18px;
                    background: var(--admission-white);
                    box-shadow: 0 8px 28px rgba(6,42,75,.065);
                }

                .students-admission-table-heading {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 15px;
                    min-height: 76px;
                    padding: 15px 19px;
                    border-bottom: 1px solid var(--admission-border);
                }

                .table-heading-eyebrow {
                    display: block;
                    margin-bottom: 4px;
                    color: var(--admission-orange);
                    font-size: 9px;
                    line-height: 1.2;
                    font-weight: 800;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                }

                .table-heading-title {
                    color: var(--admission-text);
                    font-size: 19px;
                    font-weight: 800;
                    line-height: 1.2;
                }

                .table-record-count {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    height: 34px;
                    padding: 0 11px;
                    border-radius: 9px;
                    color: var(--admission-muted);
                    background: var(--admission-page);
                    font-size: 11px;
                    font-weight: 700;
                }

                .table-record-count svg {
                    color: var(--admission-orange);
                }

                .students-admission-table {
                    background: transparent;
                }

                .students-admission-table .ant-table {
                    color: var(--admission-text);
                    background: transparent;
                }

                .students-admission-table .ant-table-container {
                    border: 0 !important;
                }

                .students-admission-table .ant-table-thead > tr > th {
                    color: #596979 !important;
                    background: #F7F9FB !important;
                    border-bottom: 1px solid var(--admission-border) !important;
                    font-size: 10px;
                    font-weight: 800;
                    letter-spacing: .6px;
                    white-space: nowrap;
                }

                .students-admission-dark
                .students-admission-table
                .ant-table-thead > tr > th {
                    color: #B5C0CA !important;
                    background: #18222C !important;
                }

                .students-admission-table .ant-table-tbody > tr > td {
                    color: var(--admission-text);
                    background: var(--admission-white);
                    border-bottom: 1px solid var(--admission-border);
                    font-size: 12.5px;
                }

                .students-admission-table
                .ant-table-tbody > tr:hover > td {
                    background: rgba(246,201,68,.045) !important;
                }

                /* CLICKABLE STUDENT PROFILE */

                .student-profile-trigger {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    width: 100%;
                    min-width: 190px;
                    padding: 0;
                    border: 0;
                    border-radius: 10px;
                    color: inherit;
                    background: transparent;
                    font: inherit;
                    text-align: left;
                    cursor: pointer;
                }

                .student-profile-trigger:focus-visible {
                    outline: 2px solid #EFA81C;
                    outline-offset: 4px;
                }

                .student-avatar {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    width: 43px;
                    height: 43px;
                    min-width: 43px;
                    border: 2px solid #D7B44A;
                    border-radius: 12px;
                    color: #063A6B;
                    background: #EAF2F8;
                    transition: transform .22s ease, box-shadow .22s ease;
                }

                .student-profile-trigger:hover .student-avatar {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 13px rgba(6,42,75,.18);
                }

                .student-avatar-fallback {
                    position: absolute;
                    font-size: 19px;
                }

                .student-table-photo {
                    position: absolute;
                    inset: 0;
                    display: block;
                    width: 100%;
                    height: 100%;
                    border-radius: 9px;
                    object-fit: cover;
                }

                .student-name-content {
                    display: block;
                    min-width: 0;
                }

                .student-name-content strong {
                    display: block;
                    overflow: hidden;
                    color: var(--admission-text);
                    font-size: 12.5px;
                    line-height: 1.4;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    transition: color .2s ease;
                }

                .student-profile-trigger:hover
                .student-name-content strong {
                    color: #C0750D;
                }

                .students-admission-dark
                .student-profile-trigger:hover
                .student-name-content strong {
                    color: #F6C944;
                }

                .student-name-content span {
                    display: block;
                    margin-top: 3px;
                    color: var(--admission-muted);
                    font-size: 9.5px;
                    line-height: 1.5;
                }

                .table-secondary-text {
                    color: var(--admission-muted);
                    font-size: 12px;
                }

                .grade-class-tag {
                    margin: 0;
                    border: 0;
                    border-radius: 7px;
                    color: #0F6DAB;
                    background: #EDF5FA;
                    font-size: 10.5px;
                    font-weight: 700;
                }

                .grade-class-tag .anticon {
                    margin-right: 4px;
                }

                .status-active,
                .status-inactive {
                    margin: 0;
                    border: 0;
                    border-radius: 7px;
                    font-size: 10px;
                    font-weight: 700;
                }

                .status-active {
                    color: #3F8F1C;
                    background: #EDF8E8;
                }

                .status-inactive {
                    color: #C53030;
                    background: #FFF0F0;
                }

                .student-action-space {
                    white-space: nowrap;
                }

                .student-action-space .ant-btn {
                    height: 31px;
                    padding: 0 9px;
                    border-radius: 7px;
                    font-size: 10.5px;
                    font-weight: 700;
                }

                .table-action-edit {
                    color: #063A6B;
                    border-color: #D8E6F0;
                    background: #F5F9FC;
                }

                .table-action-edit:hover {
                    color: #0F6DAB !important;
                    border-color: #0F6DAB !important;
                }

                .table-action-delete {
                    padding: 0 8px !important;
                }

                .secure-btn {
                    border-color: #C0750D !important;
                    background: #C0750D !important;
                    box-shadow: 0 4px 10px rgba(192,117,13,.14);
                }

                /* LOADING */

                .students-admission-loader {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    min-height: 310px;
                    color: var(--admission-muted);
                    font-size: 12px;
                }

                .students-admission-loader-ring {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .students-admission-loader-ring span {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #C0750D;
                    animation: admissionLoader 1.1s infinite ease-in-out;
                }

                .students-admission-loader-ring span:nth-child(2) {
                    animation-delay: .15s;
                    background: #EFA81C;
                }

                .students-admission-loader-ring span:nth-child(3) {
                    animation-delay: .3s;
                    background: #063A6B;
                }

                @keyframes admissionLoader {
                    0%, 80%, 100% {
                        transform: scale(.65);
                        opacity: .45;
                    }
                    40% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }

                .admission-download-section {
                    margin-top: 14px;
                }

                /* RESPONSIVE */

                @media (max-width: 900px) {
                    .students-admission-page {
                        padding: 12px;
                    }

                    .students-admission-header-content {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .students-admission-year {
                        width: 100%;
                    }

                    .students-admission-toolbar {
                        align-items: stretch;
                        flex-direction: column;
                    }

                    .students-admission-toolbar-left {
                        width: 100%;
                    }

                    .students-admission-search {
                        width: 100%;
                    }

                    .students-admission-toolbar-actions {
                        justify-content: flex-end;
                    }
                }

                @media (max-width: 600px) {
                    .students-admission-page {
                        padding: 8px;
                    }

                    .students-admission-header {
                        border-radius: 16px;
                    }

                    .students-admission-header-content {
                        min-height: auto;
                        padding: 19px 16px;
                    }

                    .students-admission-title-group {
                        align-items: flex-start;
                        gap: 12px;
                    }

                    .students-admission-icon {
                        width: 45px;
                        height: 45px;
                        min-width: 45px;
                        border-radius: 12px;
                        font-size: 20px;
                    }

                    .students-admission-title {
                        font-size: 22px !important;
                    }

                    .students-admission-subtitle {
                        font-size: 11px;
                    }

                    .students-admission-toolbar {
                        padding: 10px;
                        border-radius: 13px;
                    }

                    .students-admission-toolbar-left {
                        align-items: stretch;
                        flex-direction: column;
                    }

                    .students-admission-toolbar-actions {
                        width: 100%;
                    }

                    .students-admission-toolbar-actions > * {
                        flex: 1;
                    }

                    .students-admission-table-section {
                        border-radius: 13px;
                    }

                    .students-admission-table-heading {
                        padding: 13px;
                    }

                    .table-heading-title {
                        font-size: 16px;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .student-avatar,
                    .student-name-content strong {
                        transition: none;
                    }

                    .students-admission-loader-ring span {
                        animation: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default StudentsAdmission;
